import React, { useState, useEffect } from 'react';
import { imagePreloader } from '../utils/imagePreloader';

interface OptimizedImageProps {
  src: string;
  alt?: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({ 
  src, 
  alt = '', 
  className = '', 
  onLoad,
  onError 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>('');

  useEffect(() => {
    let mounted = true;
    let hasCalledOnLoad = false;

    const loadImage = async () => {
      try {
        // Check if already cached
        const cachedImage = imagePreloader.getCachedImage(src);
        if (cachedImage) {
          if (mounted && !hasCalledOnLoad) {
            setImageSrc(src);
            setIsLoaded(true);
            hasCalledOnLoad = true;
            onLoad?.();
          }
          return;
        }

        // Preload the image
        await imagePreloader.preloadImage(src);
        
        if (mounted && !hasCalledOnLoad) {
          setImageSrc(src);
          setIsLoaded(true);
          hasCalledOnLoad = true;
          onLoad?.();
        }
      } catch (error) {
        if (mounted && !hasCalledOnLoad) {
          setHasError(true);
          hasCalledOnLoad = true;
          onError?.();
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
    };
  }, [src]); // Remove onLoad and onError from dependencies to prevent loops

  if (hasError) {
    return (
      <div className={`flex items-center justify-center bg-slate-800 ${className}`}>
        <span className="text-slate-500 text-sm">Failed to load image</span>
      </div>
    );
  }

  return (
    <>
      {!isLoaded && (
        <div className={`flex items-center justify-center bg-slate-800 animate-pulse ${className}`}>
          <svg className="w-8 h-8 text-slate-600" fill="none" viewBox="0 0 24 24">
            <path 
              stroke="currentColor" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
        </div>
      )}
      {isLoaded && (
        <img
          src={imageSrc}
          alt={alt}
          className={className}
          style={{ display: isLoaded ? 'block' : 'none' }}
        />
      )}
    </>
  );
};

export default OptimizedImage;